<template>
  <div class="companies-list-container">
    <b-row>
      <b-col cols="12" xl="12">
        <transition name="slide">
          <b-card>
            <template #header>
              <div class="d-flex align-items-center justify-content-between">
                <h5 class="m-0">Empresas</h5>
                <div class="d-flex align-items-center">
                  <b-button
                    v-if="isWhitelabelView && selectedCompanies.length > 0"
                    variant="info"
                    size="sm"
                    class="mr-2"
                    @click="openReportSidebar"
                  >
                    <i class="fas fa-file-alt mr-1"></i>
                    RELATÓRIOS ({{ selectedCompanies.length }})
                  </b-button>
                  <b-button
                    variant="primary"
                    size="sm"
                    @click="onNewCompanyClicked"
                  >
                    Nova Empresa
                  </b-button>
                </div>
              </div>
            </template>

            <b-card-body>
              <div class="filters-section">
                <b-form-group label="Pesquisar" class="search-filter">
                  <b-input-group>
                    <b-input-group-prepend>
                      <b-input-group-text>
                        <i class="fas fa-search" aria-hidden="true"></i>
                      </b-input-group-text>
                    </b-input-group-prepend>
                    <b-form-input
                      v-model="filterText"
                      type="search"
                      placeholder="Pesquisar por nome da empresa"
                      @input="debounceFilter"
                      @keyup.enter="applyFilter"
                      @search="applyFilter"
                    ></b-form-input>
                    <b-input-group-append>
                      <b-button
                        :disabled="!filterText"
                        @click="clearFilter"
                        variant="outline-secondary"
                        >Limpar</b-button
                      >
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>
              </div>

              <div class="table-container">
                <div class="table-wrapper">
                  <b-table
                    :items="data.docs"
                    :fields="filteredHeaders"
                    :busy="loading"
                    borderless
                    hover
                    responsive
                    show-empty
                  >
                    <template #head(select)>
                      <b-form-checkbox
                        v-model="selectAll"
                        @change="toggleSelectAll"
                        :indeterminate="isIndeterminate"
                      ></b-form-checkbox>
                    </template>
                    <template #cell(select)="cell">
                      <b-form-checkbox
                        v-model="selectedCompanies"
                        :value="cell.item"
                        @click.native.stop
                      ></b-form-checkbox>
                    </template>
                    <template #cell(id)="cell">
                      {{ cell.item._id }}
                    </template>
                    <template #cell(name)="cell">
                      <span class="company-name">{{
                        cell.item.fantasyName
                      }}</span>
                    </template>
                    <template #cell(managedBy)="cell">
                      {{
                        cell.item.managedBy && cell.item.managedBy.fantasyName
                      }}
                    </template>
                    <template #cell(status)="cell">
                      <b-badge :variant="getBadge(cell.item.active)">
                        {{ cell.item.active ? "Ativa" : "Inativa" }}
                      </b-badge>
                    </template>
                    <template #cell(actions)="cell">
                      <div class="actions-wrapper">
                        <b-button
                          size="sm"
                          class="action-btn edit-btn"
                          variant="link"
                          title="Editar"
                          @click.stop="edit(cell.item)"
                        >
                          <i class="fa fa-edit" aria-hidden="true"></i>
                        </b-button>
                        <b-button
                          size="sm"
                          class="action-btn"
                          :class="
                            cell.item.active
                              ? 'status-active-btn'
                              : 'status-inactive-btn'
                          "
                          variant="link"
                          :title="cell.item.active ? 'Inativar' : 'Reativar'"
                          @click.stop="onToggleActive(cell.item)"
                        >
                          <i class="fa fa-power-off" aria-hidden="true"></i>
                        </b-button>
                        <b-button
                          size="sm"
                          class="action-btn dashboard-btn"
                          variant="link"
                          title="Dashboard"
                          @click.stop="showDashboard(cell.item)"
                        >
                          <i
                            class="fas fa-tachometer-alt"
                            aria-hidden="true"
                          ></i>
                        </b-button>
                        <b-dropdown
                          variant="link"
                          size="sm"
                          no-caret
                          title="Configurações"
                          boundary="viewport"
                          class="config-dropdown"
                          toggle-class="action-btn config-btn"
                          right
                        >
                          <template #button-content>
                            <i class="fa fa-cog" aria-hidden="true"></i>
                          </template>

                          <b-dropdown-item
                            @click.stop="onClassificationParams(cell.item)"
                          >
                            Parâmetros de Classificação
                          </b-dropdown-item>
                          <b-dropdown-item
                            @click.stop="onResponsesParams(cell.item)"
                          >
                            Respostas
                          </b-dropdown-item>
                          <b-dropdown-item
                            @click.stop="onModulesConfig(cell.item)"
                            >Módulos</b-dropdown-item
                          >
                          <b-dropdown-item
                            @click.stop="onAccessProfiles(cell.item)"
                            >Perfis de Acesso</b-dropdown-item
                          >
                          <b-dropdown-item
                            @click.stop="onRelationships(cell.item)"
                            >Relacionamentos</b-dropdown-item
                          >
                          <b-dropdown-item @click.stop="onCommittee(cell.item)"
                            >Comitês</b-dropdown-item
                          >
                        </b-dropdown>
                      </div>
                    </template>

                    <template #table-busy>
                      <div class="text-center py-5">
                        <b-spinner></b-spinner>
                        <span class="ml-2">Carregando...</span>
                      </div>
                    </template>
                    <template #empty>
                      <div class="text-muted text-center py-5">
                        <i class="fas fa-check-circle fa-2x"></i>
                        <p class="mt-3">Nenhuma empresa encontrada</p>
                      </div>
                    </template>
                  </b-table>
                </div>
              </div>

              <nav class="pagination-nav">
                <b-pagination
                  :value="data.page"
                  :total-rows="data.total"
                  :per-page="data.limit"
                  prev-text="Anterior"
                  next-text="Próximo"
                  align="center"
                  @input="handlePageChange"
                ></b-pagination>
              </nav>
            </b-card-body>
          </b-card>
        </transition>
      </b-col>
    </b-row>

    <!-- Sidebar para Relatórios -->
    <transition name="sidebar-slide">
      <div
        v-if="showReportSidebar"
        class="company-sidebar-overlay"
        @click="closeReportSidebar"
      >
        <div class="company-sidebar report-sidebar" @click.stop>
          <div class="sidebar-header">
            <h4 class="m-0">
              <i class="fas fa-file-alt mr-2"></i>Gerar Relatórios
            </h4>
            <b-button
              variant="link"
              class="close-btn"
              @click="closeReportSidebar"
            >
              <i class="fa fa-times"></i>
            </b-button>
          </div>
          <div class="sidebar-content">
            <!-- Info de empresas selecionadas -->
            <b-alert show variant="info" class="mb-4">
              <i class="fas fa-building mr-2"></i>
              <strong>{{ selectedCompanies.length }}</strong> empresa(s)
              selecionada(s)
            </b-alert>

            <!-- Seleção de período -->
            <b-form-group label="Período" class="mb-4">
              <b-row>
                <b-col cols="6">
                  <label class="small text-muted">Mês/Ano Inicial</label>
                  <b-form-input
                    v-model="reportFilters.startMonth"
                    type="month"
                  ></b-form-input>
                </b-col>
                <b-col cols="6">
                  <label class="small text-muted">Mês/Ano Final</label>
                  <b-form-input
                    v-model="reportFilters.endMonth"
                    type="month"
                  ></b-form-input>
                </b-col>
              </b-row>
            </b-form-group>

            <!-- Seleção de relatórios -->
            <b-form-group label="Selecione os Relatórios" class="mb-4">
              <div class="report-options">
                <b-form-checkbox
                  v-model="selectAllReports"
                  @change="toggleSelectAllReports"
                  class="mb-2 font-weight-bold"
                >
                  Selecionar Todos
                </b-form-checkbox>
                <hr class="my-2" />
                <b-form-checkbox-group
                  v-model="selectedReportTypes"
                  :options="reportTypeOptions"
                  stacked
                  class="report-checkbox-group"
                ></b-form-checkbox-group>
              </div>
            </b-form-group>

            <!-- Formatos de saída -->
            <b-form-group label="Formatos de Saída" class="mb-4">
              <b-form-checkbox-group
                v-model="selectedFormats"
                :options="formatOptions"
                class="d-flex"
              ></b-form-checkbox-group>
            </b-form-group>

            <!-- Progresso de geração -->
            <div v-if="isGenerating" class="generation-progress mb-4">
              <b-progress
                :value="generationProgress"
                :max="100"
                show-progress
                animated
                variant="success"
              ></b-progress>
              <p class="text-center mt-2 text-muted small">
                {{ generationStatus }}
              </p>
            </div>

            <!-- Botões de ação -->
            <div class="d-flex justify-content-end mt-4">
              <b-button
                variant="outline-secondary"
                class="mr-2"
                @click="closeReportSidebar"
                :disabled="isGenerating"
              >
                Cancelar
              </b-button>
              <b-button
                variant="primary"
                @click="generateReports"
                :disabled="!canGenerate || isGenerating"
              >
                <b-spinner small v-if="isGenerating" class="mr-1"></b-spinner>
                <i v-else class="fas fa-download mr-1"></i>
                {{ isGenerating ? "Gerando..." : "Gerar Relatórios" }}
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { getCompanies, setCompanyActive } from "../../api/company";
import {
  processingTimeReport,
  committeeReport,
  denouncedUsersReport,
  ombudsmanProcessingTimeReport,
  timeCostReport,
  generatePdfReport,
} from "../../api/reports";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import moment from "moment";

export default {
  name: "CompaniesList",
  components: {},
  data() {
    return {
      showReportSidebar: false,
      selectedCompanies: [],
      selectAll: false,
      headers: [
        {
          key: "select",
          label: "",
          thStyle: { width: "50px !important", textAlign: "center" },
          tdClass: "align-middle text-center",
        },
        { key: "id", thStyle: { width: "120px !important" } },
        { key: "name", label: "Nome" },
        { key: "managedBy", label: "Gestão Terceirizada" },
        {
          key: "status",
          label: "Status",
          thStyle: { width: "120px !important" },
        },
        {
          key: "actions",
          label: "Ações",
          thStyle: { width: "180px !important" },
        },
      ],
      data: {
        docs: [],
        total: 0,
        page: 1,
        pages: 0,
        limit: 15,
      },
      filterText: "",
      filterTimeout: null,
      loading: false,
      // Report generation
      selectedReportTypes: [],
      selectedFormats: ["pdf", "csv"],
      reportFilters: {
        startMonth: null,
        endMonth: null,
      },
      isGenerating: false,
      generationProgress: 0,
      generationStatus: "",
      selectAllReports: false,
      reportTypeOptions: [
        { value: "processingTime", text: "Tempo de Processamento" },
        { value: "committee", text: "Comitê" },
        { value: "denouncedUsers", text: "Usuários Denunciados" },
        { value: "timeCost", text: "Tempo e Custo" },
        { value: "ombudsman", text: "Ouvidoria" },
      ],
      formatOptions: [
        { value: "pdf", text: "PDF" },
        { value: "csv", text: "Excel (CSV)" },
      ],
    };
  },
  computed: {
    isWhitelabelView() {
      return this.$route.meta && this.$route.meta.whitelabel;
    },
    filteredHeaders() {
      if (!this.isWhitelabelView) {
        return this.headers.filter((h) => h.key !== "select");
      }
      return this.headers;
    },
    isIndeterminate() {
      return (
        this.selectedCompanies.length > 0 &&
        this.selectedCompanies.length < this.data.docs.length
      );
    },
    canGenerate() {
      return (
        this.selectedCompanies.length > 0 &&
        this.selectedReportTypes.length > 0 &&
        this.selectedFormats.length > 0
      );
    },
  },
  watch: {
    "data.docs": function () {
      // Reset selection when data changes
      this.selectedCompanies = [];
      this.selectAll = false;
    },
    selectedReportTypes: function (val) {
      this.selectAllReports = val.length === this.reportTypeOptions.length;
    },
  },
  mounted() {
    this.fetchCompanies();
    this.initDefaultDates();
  },
  methods: {
    initDefaultDates() {
      const now = moment();
      this.reportFilters.endMonth = now.format("YYYY-MM");
      this.reportFilters.startMonth = now
        .subtract(1, "months")
        .format("YYYY-MM");
    },

    toggleSelectAll(checked) {
      if (checked) {
        this.selectedCompanies = [...this.data.docs];
      } else {
        this.selectedCompanies = [];
      }
    },

    toggleSelectAllReports(checked) {
      if (checked) {
        this.selectedReportTypes = this.reportTypeOptions.map(
          (opt) => opt.value
        );
      } else {
        this.selectedReportTypes = [];
      }
    },

    openReportSidebar() {
      this.showReportSidebar = true;
    },

    closeReportSidebar() {
      this.showReportSidebar = false;
      this.generationProgress = 0;
      this.generationStatus = "";
    },

    async generateReports() {
      if (!this.canGenerate) return;

      this.isGenerating = true;
      this.generationProgress = 0;

      try {
        const zip = new JSZip();
        const totalSteps =
          this.selectedCompanies.length *
          this.selectedReportTypes.length *
          this.selectedFormats.length;
        let currentStep = 0;

        // Create folder structure
        const pdfFolder = zip.folder("PDF");
        const csvFolder = zip.folder("Excel");

        // Get date range for filtering
        const startDate = this.reportFilters.startMonth
          ? moment(this.reportFilters.startMonth).startOf("month").toISOString()
          : null;
        const endDate = this.reportFilters.endMonth
          ? moment(this.reportFilters.endMonth).endOf("month").toISOString()
          : null;

        for (const company of this.selectedCompanies) {
          const companyName = this.sanitizeFileName(
            company.fantasyName || company.name || `Empresa_${company._id}`
          );

          // Create company folders
          const companyPdfFolder = pdfFolder.folder(companyName);
          const companyCsvFolder = csvFolder.folder(companyName);

          for (const reportType of this.selectedReportTypes) {
            this.generationStatus = `Gerando ${this.getReportTypeName(
              reportType
            )} para ${companyName}...`;

            try {
              const reportData = await this.fetchReportData(
                reportType,
                company._id,
                startDate,
                endDate
              );

              if (reportData) {
                // Generate real PDF report via backend
                if (this.selectedFormats.includes("pdf")) {
                  const pdfBlob = await this.generatePDF(
                    reportType,
                    reportData,
                    company
                  );
                  if (pdfBlob) {
                    companyPdfFolder.file(`${reportType}.pdf`, pdfBlob);
                  }
                  currentStep++;
                  this.generationProgress = Math.round(
                    (currentStep / totalSteps) * 100
                  );
                }

                // Generate CSV
                if (this.selectedFormats.includes("csv")) {
                  const csvContent = this.generateCSV(
                    reportType,
                    reportData,
                    company
                  );
                  if (csvContent) {
                    companyCsvFolder.file(`${reportType}.csv`, csvContent);
                  }
                  currentStep++;
                  this.generationProgress = Math.round(
                    (currentStep / totalSteps) * 100
                  );
                }
              }
            } catch (error) {
              console.error(
                `Erro ao gerar relatório ${reportType} para ${companyName}:`,
                error
              );
            }
          }
        }

        this.generationStatus = "Compactando arquivos...";

        // Generate ZIP file
        const zipBlob = await zip.generateAsync({ type: "blob" });
        const timestamp = moment().format("YYYYMMDD_HHmmss");
        saveAs(zipBlob, `relatorios_${timestamp}.zip`);

        this.$bvToast.toast("Relatórios gerados com sucesso!", {
          title: "Sucesso",
          variant: "success",
          solid: true,
        });

        this.closeReportSidebar();
      } catch (error) {
        console.error("Erro ao gerar relatórios:", error);
        this.$bvToast.toast("Erro ao gerar relatórios. Tente novamente.", {
          title: "Erro",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.isGenerating = false;
      }
    },

    async fetchReportData(reportType, companyId, startDate, endDate) {
      try {
        let response;
        switch (reportType) {
          case "processingTime":
            response = await processingTimeReport(companyId);
            break;
          case "committee":
            response = await committeeReport(companyId);
            break;
          case "denouncedUsers":
            response = await denouncedUsersReport(companyId);
            break;
          case "timeCost":
            response = await timeCostReport(companyId, startDate, endDate);
            break;
          case "ombudsman":
            response = await ombudsmanProcessingTimeReport(companyId);
            break;
          default:
            return null;
        }
        return response?.data;
      } catch (error) {
        console.error(
          `Erro ao buscar dados do relatório ${reportType}:`,
          error
        );
        return null;
      }
    },

    async generatePDF(reportType, data, company) {
      try {
        const companyName =
          company.fantasyName || company.name || `Empresa ${company._id}`;
        const reportName = this.getReportTypeName(reportType);
        const items = this.extractReportItems(reportType, data);

        if (items.length === 0) return null;

        const headers = this.getReportHeaders(reportType);

        // Format rows for the PDF
        const rows = items.map((item) =>
          this.formatReportRow(reportType, item)
        );

        // Build period string
        let period = "";
        if (this.reportFilters.startMonth && this.reportFilters.endMonth) {
          period = `${moment(this.reportFilters.startMonth).format(
            "MM/YYYY"
          )} a ${moment(this.reportFilters.endMonth).format("MM/YYYY")}`;
        }

        // Call backend to generate real PDF
        const response = await generatePdfReport({
          title: reportName,
          subtitle: companyName,
          headers: headers,
          rows: rows,
          period: period,
          totalRecords: items.length,
        });

        // Return the blob data from the response
        return response.data;
      } catch (error) {
        console.error("Erro ao gerar PDF:", error);
        return null;
      }
    },

    generateCSV(reportType, data, company) {
      try {
        const items = this.extractReportItems(reportType, data);
        if (items.length === 0) return null;

        const headers = this.getReportHeaders(reportType);
        const separator = ";";
        const bom = "\ufeff";

        const escapeCsv = (value) => {
          if (value === null || value === undefined) return "";
          value = String(value);
          const needsQuotes = /[",\n\r;]/.test(value);
          const escaped = value.replace(/"/g, '""');
          return needsQuotes ? `"${escaped}"` : escaped;
        };

        const rows = items.map((item) => {
          const row = this.formatReportRow(reportType, item);
          return row.map(escapeCsv).join(separator);
        });

        return bom + headers.join(separator) + "\r\n" + rows.join("\r\n");
      } catch (error) {
        console.error("Erro ao gerar CSV:", error);
        return null;
      }
    },

    extractReportItems(reportType, data) {
      if (!data) return [];

      switch (reportType) {
        case "processingTime":
        case "committee":
          return data.complaints || [];
        case "denouncedUsers":
          return (
            data.denouncedUsers ||
            data.denounced ||
            (Array.isArray(data) ? data : [])
          );
        case "timeCost":
          return data.complaints || [];
        case "ombudsman":
          return data.ombudsmans || data.ombudsmen || data.records || [];
        default:
          return Array.isArray(data) ? data : [];
      }
    },

    getReportHeaders(reportType) {
      switch (reportType) {
        case "processingTime":
          return ["Protocolo", "Status", "Criado em", "Atualizado em", "Tempo"];
        case "committee":
          return ["Protocolo", "Status", "Relator", "Membros"];
        case "denouncedUsers":
          return ["Nome", "Departamento", "Qtd. Denúncias"];
        case "timeCost":
          return ["Protocolo", "Status", "Tempo", "Custo Estimado"];
        case "ombudsman":
          return ["Protocolo", "Status", "Natureza", "Criado em"];
        default:
          return ["Dados"];
      }
    },

    formatReportRow(reportType, item) {
      const formatDate = (date) =>
        date ? moment(date).format("DD/MM/YYYY") : "-";
      const formatDuration = (ms) => {
        if (!ms) return "-";
        const hours = Math.floor(ms / (1000 * 60 * 60));
        const days = Math.floor(hours / 24);
        return days > 0 ? `${days}d ${hours % 24}h` : `${hours}h`;
      };
      const formatMinutes = (minutes) => {
        if (!minutes) return "-";
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return hours > 0 ? `${hours}h ${mins}min` : `${mins}min`;
      };
      const getStatusText = (status) => {
        const statusMap = { 0: "Nova", 1: "Processando", 2: "Concluída" };
        return statusMap[status] || status || "-";
      };

      switch (reportType) {
        case "processingTime":
          return [
            item.protocol || "-",
            getStatusText(item.status),
            formatDate(item.createdAt),
            formatDate(item.updatedAt),
            formatDuration(item.time),
          ];
        case "committee":
          return [
            item.protocol || "-",
            getStatusText(item.status),
            item.committee?.admin || "-",
            (item.committee?.users || []).join(", ") || "-",
          ];
        case "denouncedUsers":
          return [
            item.name || "-",
            item.department || item.office || "-",
            item.complaintCount || item.count || 0,
          ];
        case "timeCost":
          return [
            item.protocol || "-",
            getStatusText(item.status),
            formatMinutes(item.totalTimeSpent || item.time),
            item.totalCost
              ? `R$ ${item.totalCost.toFixed(2)}`
              : item.cost
              ? `R$ ${item.cost.toFixed(2)}`
              : "-",
          ];
        case "ombudsman":
          return [
            item.protocol || "-",
            getStatusText(item.status),
            item.classification || item.nature || "-",
            formatDate(item.createdAt),
          ];
        default:
          return [JSON.stringify(item)];
      }
    },

    getReportTypeName(reportType) {
      const names = {
        processingTime: "Tempo de Processamento",
        committee: "Comitê",
        denouncedUsers: "Usuários Denunciados",
        timeCost: "Tempo e Custo",
        ombudsman: "Ouvidoria",
      };
      return names[reportType] || reportType;
    },

    sanitizeFileName(name) {
      return name.replace(/[<>:"/\\|?*]/g, "_").substring(0, 50);
    },

    getBadge(isActive) {
      return isActive ? "success" : "secondary";
    },

    companyLink(id) {
      if (!id) {
        console.error("Company ID is undefined");
        return "empresas/";
      }
      return `empresas/${id.toString()}`;
    },

    edit(item) {
      const companyLink = this.companyLink(item._id);
      this.$router.push({ path: companyLink });
    },

    async onToggleActive(item) {
      const willActivate = !item.active;
      const confirmed = await this.$bvModal.msgBoxConfirm(
        willActivate
          ? `Confirma reativar a empresa "${item.fantasyName}"?`
          : `Confirma inativar a empresa "${item.fantasyName}"?`,
        {
          title: willActivate ? "Reativar Empresa" : "Inativar Empresa",
          size: "md",
          buttonSize: "sm",
          okVariant: willActivate ? "success" : "danger",
          okTitle: willActivate ? "Sim, Reativar" : "Sim, Inativar",
          cancelTitle: "Cancelar",
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
        }
      );
      if (!confirmed) return;
      try {
        await setCompanyActive(item._id, willActivate);
        this.$bvToast.toast(
          willActivate
            ? "Empresa reativada com sucesso!"
            : "Empresa inativada com sucesso!",
          {
            title: "Sucesso",
            toastClass: "custom-toast custom-toast-success",
            variant: "light",
            solid: true,
            autoHideDelay: 5000,
            noCloseButton: false,
          }
        );
        this.fetchCompanies();
      } catch (error) {
        console.error("Erro ao alterar status da empresa:", error);
        this.$bvToast.toast(
          "Erro ao alterar status da empresa. Tente novamente.",
          {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          }
        );
      }
    },

    showDashboard(item) {
      const companyLink = this.companyLink(item._id);
      this.$router.push({ path: `${companyLink}/dashboard` });
    },

    onClassificationParams(item) {
      const companyLink = this.companyLink(item._id);
      this.$router.push({ path: `${companyLink}/parametros/classificacao` });
    },

    onResponsesParams(item) {
      const companyLink = this.companyLink(item._id);
      this.$router.push({ path: `${companyLink}/parametros/respostas` });
    },

    onModulesConfig(item) {
      const companyLink = this.companyLink(item._id);
      this.$router.push({ path: `${companyLink}/modulos` });
    },

    onAccessProfiles(item) {
      const companyLink = this.companyLink(item._id);
      this.$router.push({ path: `${companyLink}/perfis-de-acesso` });
    },

    onRelationships(item) {
      const companyLink = this.companyLink(item._id);
      this.$router.push({ path: `${companyLink}/relacionamentos` });
    },

    onCommittee(item) {
      const companyLink = this.companyLink(item._id);
      this.$router.push({ path: `${companyLink}/comite` });
    },

    onNewCompanyClicked() {
      this.$router.push({ path: "/empresas/novo" });
    },
    clearFilter() {
      this.filterText = "";
      this.applyFilter();
    },
    debounceFilter() {
      clearTimeout(this.filterTimeout);
      this.filterTimeout = setTimeout(() => {
        this.applyFilter();
      }, 300);
    },
    applyFilter() {
      this.data.page = 1;
      this.fetchCompanies();
    },
    handlePageChange(page) {
      this.data.page = page;
      this.fetchCompanies();
    },
    async fetchCompanies() {
      this.loading = true;
      try {
        const response = await getCompanies({
          page: this.data.page,
          size: this.data.limit,
          filter:
            this.filterText && this.filterText.trim()
              ? this.filterText.trim()
              : undefined,
        });
        const payload =
          response && response.data
            ? response.data
            : { docs: [], total: 0, pages: 0 };
        const currentPage = Number(this.data.page) || 1;
        const currentLimit = Number(this.data.limit) || 15;

        if (Array.isArray(payload)) {
          const total = payload.length;
          const pages = Math.max(1, Math.ceil(total / currentLimit));
          const safePage = Math.min(Math.max(currentPage, 1), pages);
          const start = (safePage - 1) * currentLimit;

          this.data.docs = payload.slice(start, start + currentLimit);
          this.data.total = total;
          this.data.pages = pages;
          this.data.page = safePage;
          this.data.limit = currentLimit;
        } else {
          this.data.docs = payload.docs || payload.data || [];
          this.data.total =
            payload.total ||
            payload.totalCount ||
            (payload.docs
              ? payload.docs.length
              : payload.data
              ? payload.data.length
              : 0);
          this.data.pages = payload.pages || payload.totalPages || 1;
          this.data.limit =
            (Number(payload.limit) > 0 && Number(payload.limit)) ||
            currentLimit;
          this.data.page =
            (Number(payload.page) > 0 && Number(payload.page)) || currentPage;
        }
      } catch (error) {
        console.error("Erro ao carregar empresas:", error);
        this.$bvToast &&
          this.$bvToast.toast("Erro ao carregar empresas", {
            title: "Erro",
            variant: "danger",
            solid: true,
          });
        this.data.docs = [];
        this.data.total = 0;
        this.data.pages = 0;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.actions-wrapper {
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: nowrap;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  padding: 0 !important;
  margin-right: 6px;
  border: none;
  transition: all 0.2s ease;
  text-decoration: none !important;
}

.action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  filter: brightness(0.95);
}

.action-btn i {
  font-size: 1rem;
  margin: 0;
}

.edit-btn {
  background-color: #e8eaf6 !important;
  color: #3f51b5 !important;
}

.status-active-btn {
  background-color: #ffebee !important;
  color: #f44336 !important;
}

.status-inactive-btn {
  background-color: #e8f5e9 !important;
  color: #4caf50 !important;
}

.dashboard-btn {
  background-color: #e0f7fa !important;
  color: #00838f !important;
}

.config-btn {
  background-color: #eceff1 !important;
  color: #455a64 !important;
}

::v-deep .config-dropdown {
  display: inline-block;
  vertical-align: middle;
}

::v-deep .config-dropdown .btn-link.action-btn {
  box-shadow: none !important;
  outline: none !important;
}

.filters-section {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  align-items: flex-end;
}

.search-filter {
  flex: 1;
}

.filters-section .form-group {
  margin-bottom: 0;
}

.filters-section .form-control,
.filters-section .custom-select {
  border-radius: 6px;
  border: 1px solid #e9ecef;
  background-color: #fff;
  color: #495057;
  height: calc(1.5em + 1rem + 2px);
  padding: 0.5rem 1rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.filters-section .form-control:focus,
.filters-section .custom-select:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.2);
  outline: 0;
}

.filters-section .input-group-text {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-right: 0;
  border-radius: 6px 0 0 6px;
}

.table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  position: relative;
}

.table-wrapper {
  overflow-x: auto;
  overflow-y: visible;
}

.pagination-nav {
  margin-top: 1.5rem;
}

::v-deep .page-item .page-link {
  border-radius: 6px;
  margin: 0 3px;
  border: 1px solid #e9ecef;
  color: #495057;
  transition: background-color 0.2s, color 0.2s;
}

::v-deep .page-item .page-link:hover {
  background-color: #f0f2f5;
  color: #0056b3;
}

::v-deep .page-item.active .page-link {
  background-color: #007bff;
  border-color: #007bff;
  color: #fff;
}

::v-deep .page-item.disabled .page-link {
  background-color: transparent;
  color: #adb5bd;
}

::v-deep thead th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #495057;
  font-size: 0.8rem;
  text-transform: uppercase;
  border: 0;
  white-space: nowrap;
}

::v-deep tbody tr {
  border-bottom: 1px solid #f1f3f4;
  transition: background-color 0.2s ease-in-out;
}

::v-deep tbody tr:hover {
  background-color: #e9ecef !important;
}

::v-deep tbody td {
  border-top: none;
  vertical-align: middle;
  padding: 0.85rem 0.75rem;
}

.company-name,
.date-text,
.nature-text {
  color: #6c757d;
}

.action-icon {
  color: #6c757d;
  transition: color 0.2s;
}

::v-deep tbody tr:hover .action-icon {
  color: #007bff;
}

/* Estilos para o dropdown de configurações */
::v-deep .config-dropdown {
  display: inline-block;
  position: relative;
}

::v-deep .config-dropdown .btn-link {
  padding: 0.25rem 0.5rem;
  border: none;
  background: transparent;
  box-shadow: none !important;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

::v-deep .config-dropdown .btn-link:hover,
::v-deep .config-dropdown .btn-link:focus,
::v-deep .config-dropdown .btn-link:active {
  border: none;
  box-shadow: none !important;
  background: transparent;
  text-decoration: none;
  outline: none;
}

::v-deep .config-dropdown .btn-link .action-icon {
  font-size: 1.1rem;
  pointer-events: none;
}

::v-deep .dropdown-menu {
  min-width: 240px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 0.5rem 0;
  margin-top: 0.25rem;
  position: absolute !important;
  z-index: 1050 !important;
  will-change: transform;
}

::v-deep .dropdown-item {
  padding: 0.5rem 1rem;
  color: #495057;
  font-size: 0.9rem;
  transition: background-color 0.2s ease, color 0.2s ease;
  cursor: pointer;
}

::v-deep .dropdown-item:hover,
::v-deep .dropdown-item:focus {
  background-color: #f8f9fa;
  color: #007bff;
  text-decoration: none;
}

::v-deep .dropdown-item:active {
  background-color: #007bff;
  color: #fff;
}

/* Estilos do Sidebar */
.companies-list-container {
  position: relative;
}

.company-sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1040;
  display: flex;
  justify-content: flex-end;
}

.company-sidebar {
  width: 50%;
  min-width: 500px;
  max-width: 1000px;
  height: 100%;
  background-color: #fff;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
  background-color: #f8f9fa;
}

.sidebar-header h4 {
  font-weight: 600;
  color: #495057;
}

.close-btn {
  color: #6c757d;
  font-size: 1.5rem;
  padding: 0;
  line-height: 1;
  transition: color 0.2s;
}

.close-btn:hover {
  color: #007bff;
}

.sidebar-content {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
}

/* Animações do Sidebar */
.sidebar-slide-enter-active,
.sidebar-slide-leave-active {
  transition: opacity 0.3s ease;
}

.sidebar-slide-enter-active .company-sidebar,
.sidebar-slide-leave-active .company-sidebar {
  transition: transform 0.3s ease;
}

.sidebar-slide-enter,
.sidebar-slide-leave-to {
  opacity: 0;
}

.sidebar-slide-enter .company-sidebar,
.sidebar-slide-leave-to .company-sidebar {
  transform: translateX(100%);
}

@media (max-width: 992px) {
  .company-sidebar {
    width: 60%;
    min-width: 0;
  }
}

@media (max-width: 768px) {
  .company-sidebar {
    width: 90%;
  }
}

/* Estilos do Sidebar de Relatórios */
.report-sidebar {
  width: 450px;
  min-width: 400px;
  max-width: 500px;
}

.report-sidebar .sidebar-header {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  color: white;
}

.report-sidebar .sidebar-header h4 {
  color: white;
}

.report-sidebar .close-btn {
  color: white;
}

.report-sidebar .close-btn:hover {
  color: #e9ecef;
}

.report-options {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid #e9ecef;
}

.report-checkbox-group {
  max-height: 200px;
  overflow-y: auto;
}

::v-deep .report-checkbox-group .custom-control {
  padding: 0.5rem 0;
  padding-left: 1.75rem;
  border-bottom: 1px solid #e9ecef;
}

::v-deep .report-checkbox-group .custom-control:last-child {
  border-bottom: none;
}

.generation-progress {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid #e9ecef;
}

::v-deep .progress {
  height: 1.5rem;
  border-radius: 8px;
}

::v-deep .progress-bar {
  font-size: 0.8rem;
  font-weight: 600;
}

/* Checkbox na tabela */
::v-deep .custom-checkbox .custom-control-label::before,
::v-deep .custom-checkbox .custom-control-label::after {
  top: 50%;
  transform: translateY(-50%);
}

::v-deep thead .custom-checkbox {
  display: flex;
  align-items: center;
  justify-content: center;
}

::v-deep tbody .custom-checkbox {
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 576px) {
  .report-sidebar {
    width: 100%;
    min-width: 0;
  }
}
</style>
